import { useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Download, X } from "lucide-react";
import SankeyChart, { SANKEY_HEIGHT, SANKEY_WIDTH } from "./SankeyChart";
import {
  NO_RESPONSE_COLOR,
  NO_RESPONSE_NODE,
  STAGE_COLOR,
  STAGE_LABELS,
  STAGE_ORDER,
} from "../utils/applicationHistory";
import { downloadSankeyPng } from "../utils/exportSankey";

type AppLike = {
  history?: any;
  predicted_label?: string;
  date?: string;
};

type SankeyModalProps = {
  isOpen: boolean;
  onClose: () => void;
  apps: AppLike[];
};

// YYYY-MM-DD in the user's time zone (toISOString would use UTC)
function localDateStamp() {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

const LEGEND = [
  ...STAGE_ORDER.map((s) => ({ label: STAGE_LABELS[s], color: STAGE_COLOR[s] })),
  { label: NO_RESPONSE_NODE, color: NO_RESPONSE_COLOR },
];

export default function SankeyModal({ isOpen, onClose, apps }: SankeyModalProps) {
  const chartRef = useRef<SVGSVGElement>(null);
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  const today = new Date().toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  const countLabel = `${apps.length} application${apps.length === 1 ? "" : "s"}`;

  const handleDownload = async () => {
    if (!chartRef.current) return;
    setDownloading(true);
    setDownloadError(null);
    try {
      await downloadSankeyPng({
        chart: chartRef.current,
        chartWidth: SANKEY_WIDTH,
        chartHeight: SANKEY_HEIGHT,
        title: "My job hunt so far",
        subtitle: `${countLabel} · ${today}`,
        legend: LEGEND,
        filename: `caramel-application-flow-${localDateStamp()}.png`,
      });
    } catch (error) {
      console.error("Sankey download failed:", error);
      setDownloadError("Couldn't create the image. Please try again.");
    } finally {
      setDownloading(false);
    }
  };

  return (
    // Radix Dialog for focus trap / Esc / scroll lock; framer-motion for enter + exit.
    <DialogPrimitive.Root open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <AnimatePresence>
        {isOpen && (
          <DialogPrimitive.Portal forceMount>
            <motion.div
              className="fixed inset-0 z-50 flex items-center justify-center p-4"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.18 }}
            >
              <DialogPrimitive.Overlay forceMount className="absolute inset-0 bg-black/30 backdrop-blur-[2px]" />
              <DialogPrimitive.Content forceMount asChild>
                <motion.div
                  className="relative z-10 flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-[20px] border border-melt-hair bg-melt-paper font-grotesk text-melt-text1 shadow-[0_30px_60px_-30px_rgba(92,52,23,.45)] focus:outline-none"
                  initial={{ scale: 0.96, y: 12 }}
                  animate={{ scale: 1, y: 0 }}
                  exit={{ scale: 0.96, y: 12 }}
                  transition={{ duration: 0.18, ease: [0.22, 1, 0.36, 1] }}
                >
                  <div className="flex items-start justify-between gap-4 px-7 pb-2 pt-6">
                    <div>
                      <DialogPrimitive.Title className="text-[22px] font-semibold tracking-[-.02em]">
                        Application flow
                      </DialogPrimitive.Title>
                      <DialogPrimitive.Description className="mt-1.5 font-plex text-xs text-melt-text3">
                        How your {countLabel} moved between stages
                      </DialogPrimitive.Description>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <button
                        type="button"
                        onClick={handleDownload}
                        disabled={downloading || apps.length === 0}
                        className="inline-flex h-9 items-center gap-2 rounded-[9px] border border-melt-hair bg-white px-3.5 text-[13.5px] font-semibold text-melt-text1 transition-colors hover:bg-melt-paper disabled:opacity-50"
                      >
                        <Download size={16} strokeWidth={1.75} />
                        {downloading ? "Preparing…" : "Download"}
                      </button>
                      <button
                        type="button"
                        onClick={onClose}
                        aria-label="Close"
                        className="grid h-9 w-9 place-items-center rounded-[9px] text-melt-text3 transition-colors hover:bg-melt-text1/[.06] hover:text-melt-text1"
                      >
                        <X size={18} strokeWidth={1.75} />
                      </button>
                    </div>
                  </div>

                  <div className="scrollbar-thin overflow-y-auto px-7 pb-6 pt-3">
                    <div className="rounded-[14px] border border-melt-hair bg-white/60 px-4 py-3">
                      <SankeyChart ref={chartRef} apps={apps} />
                    </div>

                    <div className="mt-4 flex flex-wrap gap-x-4 gap-y-2">
                      {LEGEND.map((item) => (
                        <span key={item.label} className="inline-flex items-center gap-1.5 text-xs text-melt-text2">
                          <span className="h-2 w-2 rounded-[3px]" style={{ backgroundColor: item.color }} />
                          {item.label}
                        </span>
                      ))}
                    </div>

                    {downloadError && <p className="mt-3 text-sm text-[#9A4F33]">{downloadError}</p>}
                  </div>
                </motion.div>
              </DialogPrimitive.Content>
            </motion.div>
          </DialogPrimitive.Portal>
        )}
      </AnimatePresence>
    </DialogPrimitive.Root>
  );
}
